import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { MarketCustomColumn } from '../../types/database'

interface InlineCustomColumnEditorProps {
  /** Current value */
  value: string | null | undefined
  /** Custom column configuration (contains input_type and options) */
  customColumn: MarketCustomColumn
  /** Called when user saves a new value */
  onSave: (newValue: string | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
}

/**
 * Inline editor for custom columns in market tables.
 * Renders either a text input or dropdown based on the column's input_type configuration.
 */
export default function InlineCustomColumnEditor({
  value,
  customColumn,
  onSave,
  onCancel
}: InlineCustomColumnEditorProps) {
  const [inputValue, setInputValue] = useState(value || '')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const textContainerRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const saveTriggeredRef = useRef(false)

  // Focus input on mount
  useEffect(() => {
    if (customColumn.input_type === 'text' && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    } else if (customColumn.input_type === 'dropdown' && selectRef.current) {
      selectRef.current.focus()
    }
  }, [customColumn.input_type])

  const handleSave = useCallback(async () => {
    if (isSaving) return

    const newValue = inputValue.trim() === '' ? null : inputValue.trim()
    const oldValue = value || null

    if (newValue === oldValue) {
      onCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue)
    } catch (error) {
      setIsSaving(false)
    }
  }, [inputValue, value, onSave, onCancel, isSaving])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }, [handleSave, onCancel])

  // Handle click outside to save synchronously for text mode (fires before blur/click on new cell)
  useEffect(() => {
    if (customColumn.input_type !== 'text') return

    const handleClickOutside = (event: MouseEvent) => {
      if (textContainerRef.current && !textContainerRef.current.contains(event.target as Node)) {
        saveTriggeredRef.current = true
        handleSave()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [customColumn.input_type, handleSave])

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (!isSaving && !saveTriggeredRef.current) {
        handleSave()
      }
    }, 100)
  }, [handleSave, isSaving])

  // Handle click outside for dropdown
  useEffect(() => {
    if (customColumn.input_type !== 'dropdown') return

    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        handleSave()
      }
    }

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [customColumn.input_type, handleSave])

  // Calculate fixed position based on anchor element so popover is not clipped by overflow containers
  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect()
      const isDropdown = customColumn.input_type === 'dropdown'
      const popoverHeight = isDropdown ? 250 : 40
      const popoverWidth = isDropdown ? 180 : 140

      let top = rect.bottom + 4
      let left = rect.left

      if (top + popoverHeight > window.innerHeight) {
        top = Math.max(8, rect.top - popoverHeight - 4)
      }
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8)
      }
      if (left < 8) left = 8

      setPosition({ top, left })
    }
  }, [customColumn.input_type])

  const dropdownOptions = customColumn.options || []

  const popoverContent = customColumn.input_type === 'text' ? (
    <div
      ref={textContainerRef}
      className="fixed z-[9999] bg-white shadow-lg rounded p-1"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' as const }}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        placeholder={customColumn.column_title}
        className={`
          w-32 px-2 py-1 text-sm text-center
          border border-green-500 rounded
          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
          ${isSaving ? 'bg-gray-100 text-gray-500' : 'bg-white'}
        `}
      />
    </div>
  ) : (
    <div
      ref={popoverRef}
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg p-2 min-w-[160px] max-h-[250px] overflow-y-auto"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' as const }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-xs font-medium text-gray-500">{customColumn.column_title}</span>
        {inputValue && (
          <button
            type="button"
            onClick={() => { setInputValue(''); }}
            className="text-xs text-red-600 hover:text-red-800 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <div className="space-y-1">
        {dropdownOptions.map((option) => {
          const isSelected = inputValue === option
          return (
            <label
              key={option}
              className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                isSelected ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name={`custom-${customColumn.column_index}`}
                checked={isSelected}
                onChange={() => setInputValue(option)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-900">{option}</span>
            </label>
          )
        })}
      </div>
      {isSaving && <div className="text-xs text-gray-500 mt-2 px-1">Saving...</div>}
    </div>
  )

  return (
    <>
      <span ref={anchorRef} />
      {ReactDOM.createPortal(popoverContent, document.body)}
    </>
  )
}

